import { cn } from '../../lib/utils.js';
import { Container } from './Container.jsx';
import { Icon } from '../ui/Icon.jsx';

/**
 * Footer component
 */
export function Footer({
  logo,
  logoAlt = 'Logo',
  navigation = {},
  social = [],
  copyright,
  className,
  renderLink,
}) {
  const Link = renderLink || 'a';
  const currentYear = new Date().getFullYear();

  return (
    <footer className={cn('bg-dark-1 text-white py-16', className)}>
      <Container>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Logo & Description */}
          <div className="lg:col-span-1">
            {logo && (
              <Link href="/" className="inline-block mb-6">
                {typeof logo === 'string' ? (
                  <img src={logo} alt={logoAlt} className="h-8 w-auto" />
                ) : (
                  logo
                )}
              </Link>
            )}
            <p className="text-gray-400 text-sm leading-relaxed">
              Professionelle Webentwicklung, 3D-Modelle und AR/VR Lösungen für Ihr Business.
            </p>

            {/* Social Links */}
            {social.length > 0 && (
              <div className="flex gap-4 mt-6">
                {social.map((item, index) => (
                  <a
                    key={index}
                    href={item.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-400 hover:text-white transition-colors"
                    aria-label={item.platform}
                  >
                    <i className={`fa ${item.icon} text-lg`} aria-hidden="true" />
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* Navigation Columns */}
          {navigation.services && (
            <div>
              <div className="text-sm font-semibold uppercase tracking-wider mb-6">
                Leistungen
              </div>
              <ul className="space-y-3">
                {navigation.services.map((item, index) => (
                  <li key={index}>
                    <Link
                      href={item.href}
                      className="text-gray-400 text-sm hover:text-white transition-colors"
                    >
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {navigation.company && (
            <div>
              <div className="text-sm font-semibold uppercase tracking-wider mb-6">
                Unternehmen
              </div>
              <ul className="space-y-3">
                {navigation.company.map((item, index) => (
                  <li key={index}>
                    <Link
                      href={item.href}
                      className="text-accent text-sm hover:text-white transition-colors"
                    >
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Contact Info */}
          <div>
            <div className="text-sm font-semibold uppercase tracking-wider mb-6">
              Kontakt
            </div>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <Icon name="mail" size="sm" className="text-accent mt-1" />
                <a
                  href="mailto:info@codeschnipsel.dev"
                  className="text-gray-400 text-sm hover:text-white transition-colors"
                >
                  sebastian@codeschnipsel.dev
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Icon name="phone" size="sm" className="text-accent mt-1" />
                <a
                  href="tel:+4923116730433"
                  className="text-gray-400 text-sm hover:text-white transition-colors"
                >
                  +49 23116730433
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Icon name="location" size="sm" className="text-accent mt-1" />
                <span className="text-gray-400 text-sm">
                  Dorneystrasse 45<br />
                  44149 Dortmund
                </span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-800 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-gray-500 text-sm">
            {copyright || `© ${currentYear} Codeschnipsel. Alle Rechte vorbehalten.`}
          </p>

          {navigation.legal && (
            <nav className="flex gap-6">
              {navigation.legal.map((item, index) => (
                <Link
                  key={index}
                  href={item.href}
                  className="text-gray-500 text-sm hover:text-white transition-colors"
                >
                  {item.label}
                </Link>
              ))}
            </nav>
          )}
        </div>
      </Container>
    </footer>
  );
}

export default Footer;
